import { decodeMail } from "@/lib/email";
import { supabase } from "@/integrations/supabase/client";

export const PORTAL_TOKEN_KEY = "kundenportal:token";

export interface PortalUser {
  username: string;
  company: string;
  pageSlug: string;
  mustChangePassword: boolean;
  isInternal?: boolean;
  isTreffpunktwerk?: boolean;
  isMaster?: boolean;
}

interface PortalResponse {
  token?: string;
  user?: PortalUser;
  error?: string;
}

const call = async (body: Record<string, unknown>): Promise<PortalResponse> => {
  const { data, error } = await supabase.functions.invoke("portal-auth", { body });
  if (error) {
    let details = "";
    try {
      const ctx = (error as { context?: { text?: () => Promise<string> } }).context;
      details = (await ctx?.text?.()) ?? "";
    } catch {
      /* ignorieren */
    }
    try {
      const parsed = JSON.parse(details) as PortalResponse;
      if (parsed?.error) return { error: parsed.error };
    } catch {
      /* ignorieren */
    }
    return { error: "Anmeldung momentan nicht möglich. Bitte später erneut versuchen." };
  }
  return (data ?? {}) as PortalResponse;
};

/**
 * Kundenkonten: Token nur im Arbeitsspeicher (Neuladen = neue Anmeldung).
 * Interne Konten: Token zusätzlich im sessionStorage – überlebt das Neuladen,
 * endet aber beim Schliessen des Tabs und spätestens nach 24 Stunden.
 */
export const PORTAL_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

let memoryToken: string | null = null;

// Alte, dauerhaft gespeicherte Tokens einmalig entfernen.
try {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
} catch {
  /* ignorieren */
}

const readPersisted = (): string | null => {
  try {
    const raw = sessionStorage.getItem(PORTAL_TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: string; expiresAt?: number };
    if (!parsed?.token || !parsed.expiresAt || parsed.expiresAt < Date.now()) {
      sessionStorage.removeItem(PORTAL_TOKEN_KEY);
      return null;
    }
    return parsed.token;
  } catch {
    return null;
  }
};

export const getStoredToken = () => memoryToken ?? readPersisted();

export const storeToken = (token: string, options?: { persist?: boolean }) => {
  memoryToken = token;
  if (!options?.persist) return;
  try {
    sessionStorage.setItem(
      PORTAL_TOKEN_KEY,
      JSON.stringify({ token, expiresAt: Date.now() + PORTAL_SESSION_TTL_MS }),
    );
  } catch {
    /* ignorieren */
  }
};

export const clearToken = () => {
  memoryToken = null;
  cachedUser = null;
  cachedUserPromise = null;
  try {
    sessionStorage.removeItem(PORTAL_TOKEN_KEY);
  } catch {
    /* ignorieren */
  }
};

/**
 * Aufgelöste Portal-Sitzung im Speicher merken, damit Navigationen nicht bei
 * jedem Seitenwechsel erneut beim Server nachfragen müssen.
 */
let cachedUser: PortalUser | null = null;
let cachedUserPromise: Promise<PortalUser | null> | null = null;

/** Sofort verfügbarer, bereits geprüfter Benutzer (oder null). */
export const getCachedPortalUser = () => cachedUser;

/** Portal-Benutzer holen – beim ersten Mal via Server, danach aus dem Cache. */
export const resolvePortalUser = (): Promise<PortalUser | null> => {
  if (cachedUser) return Promise.resolve(cachedUser);
  if (cachedUserPromise) return cachedUserPromise;
  const token = getStoredToken();
  if (!token) return Promise.resolve(null);
  cachedUserPromise = portalSession(token)
    .then((res) => {
      cachedUser = res.user ?? null;
      return cachedUser;
    })
    .catch(() => null)
    .finally(() => {
      cachedUserPromise = null;
    });
  return cachedUserPromise;
};

/** Nur dieses interne Konto sieht die Portal-Übersichtsseite. */
export const PORTAL_START_USERNAME = decodeMail("yannick.althaus");

/** Darf das Konto die Übersichtsseite /kundenportal/start sehen? */
export const canSeePortalStart = (user: PortalUser) =>
  Boolean(user.isInternal) && user.username.trim().toLowerCase() === PORTAL_START_USERNAME;

/** Dieses interne Konto startet auf dem Rundgang und sieht keine "Bereiche". */
export const RUNDGANG_HOME_USERNAME = "althaus";

/** Konto, das nur den Rundgang ohne Bereiche nutzt. */
export const isRundgangOnlyUser = (user: PortalUser) =>
  Boolean(user.isInternal) && user.username.trim().toLowerCase() === RUNDGANG_HOME_USERNAME;

/** Kanonische Zielseite eines Portal-Kontos (rein rollenbasiert). */
export const portalHomePath = (user: PortalUser) => {
  if (user.isTreffpunktwerk) return "/rundgang/bereiche";
  if (user.isInternal) {
    if (isRundgangOnlyUser(user)) return "/rundgang";
    return canSeePortalStart(user) ? "/kundenportal/start" : "/messe/interaktiv";
  }
  return `/kundenportal/${user.pageSlug}`;
};


export const portalLogin = (username: string, password: string) =>
  call({ action: "login", username, password });

export const portalSession = (token: string) => call({ action: "session", token });

export const portalChangePassword = (
  token: string,
  currentPassword: string,
  newPassword: string,
) => call({ action: "change-password", token, currentPassword, newPassword });
